/**
 * Sprint 22 — v3 chat runtime hook.
 *
 * Ported from the Sprint 3.2 `useKuvioChatRuntime` (console/src/hooks/use-chat.ts)
 * and extended for the cockpit chat:
 *  - sends `scope` + `page_context` body params (scope generalisation, S22.02)
 *  - sends the `x-kuvio-host: in-app-chat` header so tool calls audit with the
 *    in-app-chat actor host (S22.12)
 *
 * Like the legacy hook it uses the low-level useChat + useAISDKRuntime (not the
 * high-level useChatRuntime) so the raw AI SDK messages are serialisable for
 * D1-backed persistence, and wraps the transport in a stable Proxy so useChat
 * never re-keys mid-conversation.
 */

import { useEffect, useMemo, useRef } from "react";
import { useChat, type UIMessage } from "@ai-sdk/react";
import { AssistantChatTransport, useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import type { AssistantRuntime } from "@assistant-ui/react";
import { getAccessToken } from "../../lib/auth";
import type { ChatScope, PageContext } from "../components/chat/ChatRailContext";

/** Derive the AI proxy URL from the MCP endpoint. */
function aiChatEndpoint(agentNodeId?: string): string {
  const env = import.meta.env as { VITE_MCP_ENDPOINT?: string };
  const mcp = env.VITE_MCP_ENDPOINT ?? "https://dev.kuvio.ai/mcp";
  const base = mcp.replace(/\/mcp\/?$/, "");
  if (agentNodeId) return `${base}/ai/agent/${agentNodeId}/chat`;
  return `${base}/ai/chat`;
}

export interface V3ChatOptions {
  /** Generated client-side; sent to the proxy so usage logs share an id. */
  conversationId: string;
  /** Forwarded so the Worker routes to the right WorkspaceDO once auth lands. */
  workspaceId?: string;
  /** When set, routes to /ai/agent/:nodeId/chat instead of /ai/chat. */
  agentNodeId?: string;
  /** Session scope classification (stored on the conversation row). */
  scope?: ChatScope;
  /** Generalised "what is the user looking at" focus anchor for the prompt. */
  pageContext?: PageContext | null;
}

export interface V3ChatResult {
  runtime: AssistantRuntime;
  chatMessages: UIMessage[];
  setChatMessages: (
    messages: UIMessage[] | ((messages: UIMessage[]) => UIMessage[]),
  ) => void;
  status: "submitted" | "streaming" | "ready" | "error";
  error: Error | undefined;
  /**
   * Sprint 23.9 (Fix 2) — clears the AI SDK's internal `error`/`status:"error"`.
   * `useChat` owns that state and doesn't reset it on a transport re-key, so the
   * panel calls this on scope change to wipe a prior scope's error (Q3/Q8).
   */
  clearError: () => void;
  /**
   * Sprint 23.9 (Fix 3) — re-runs the last turn for the inline retry button.
   * Our worker streams from `body.messages` regardless of the regenerate
   * trigger, so this correctly resends whether or not a partial assistant
   * message exists, without duplicating the user's message.
   */
  regenerate: () => void;
}

export function useV3ChatRuntime({
  conversationId,
  workspaceId,
  agentNodeId,
  scope,
  pageContext,
}: V3ChatOptions): V3ChatResult {
  const transportRef = useRef<AssistantChatTransport<UIMessage>>(null!);

  // Only the worker-relevant scope fields (drop the client-only `label`).
  const scopeKey = scope ? `${scope.kind}:${scope.id ?? ""}:${scope.type_slug ?? ""}` : "";
  const pageKey = pageContext ? `${pageContext.kind}:${pageContext.id ?? ""}:${pageContext.name}` : "";

  const latestTransport = useMemo(
    () =>
      new AssistantChatTransport({
        api: aiChatEndpoint(agentNodeId),
        headers: (): Record<string, string> => {
          const token = getAccessToken();
          return {
            "x-kuvio-host": "in-app-chat",
            ...(token ? { authorization: `Bearer ${token}` } : {}),
          };
        },
        body: {
          conversation_id: conversationId,
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
          ...(scope
            ? { scope: { kind: scope.kind, id: scope.id, type_slug: scope.type_slug } }
            : {}),
          ...(pageContext ? { page_context: pageContext } : {}),
        },
      }),
    // scopeKey/pageKey stand in for the object identities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversationId, workspaceId, agentNodeId, scopeKey, pageKey],
  );

  if (transportRef.current === null) {
    transportRef.current = latestTransport;
  }

  useEffect(() => {
    transportRef.current = latestTransport;
  });

  const stableTransport = useMemo(
    () =>
      new Proxy(latestTransport, {
        get(_, prop) {
          const target = transportRef.current;
          const val = target[prop as keyof typeof target];
          return typeof val === "function" ? (val as Function).bind(target) : val;
        },
      }),
    // Intentionally empty — the proxy is stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const chat = useChat({ transport: stableTransport });
  const runtime = useAISDKRuntime(chat);

  return {
    runtime,
    chatMessages: chat.messages,
    setChatMessages: chat.setMessages,
    status: chat.status,
    error: chat.error,
    clearError: chat.clearError,
    regenerate: chat.regenerate,
  };
}
